import { useMemo } from 'react'

import { ChartData, ChartOptions } from 'chart.js'
import { Chart } from 'react-chartjs-2'

import { BID_ANALYSIS_CHART_HEIGHT } from 'components/reports/Metrics/constants'
import {
  BucketLabel,
  BucketSeries,
  bucketBarChartPlugins,
  defaultValueFormatter,
  ValueFormatter,
} from 'helpers/bucketBarChart.helpers'

import { BAR_STYLE } from './chartConfig'

const LABEL_PADDING_TOP = 20
const TWO_LINE_LABEL_PADDING_TOP = 34

export type { BucketLabel, BucketSeries }

interface Props {
  labels: BucketLabel[]
  series: BucketSeries[]
  // Left blank when the card explains the axis itself.
  xAxisLabel?: string
  yAxisLabel?: string
  height?: number
  // How a bar's value is written on the bar and in the tooltip. Counts by default.
  valueFormatter?: ValueFormatter
  valueLabelLines?: (value: number, index: number) => string[]
  valueLabelColor?: string
  // How far the x-axis labels may tilt to avoid overlapping. Upright by default.
  xTickMaxRotation?: number
}

export const BucketBarChart = ({
  labels,
  series,
  xAxisLabel,
  yAxisLabel = 'Number of jobs',
  height = BID_ANALYSIS_CHART_HEIGHT,
  valueFormatter = defaultValueFormatter,
  valueLabelLines,
  valueLabelColor,
  xTickMaxRotation = 0,
}: Props) => {
  const data = useMemo<ChartData<'bar', number[], BucketLabel>>(
    () => ({
      labels,
      datasets: series.map((s) => ({
        ...BAR_STYLE,
        type: 'bar' as const,
        label: s.label,
        data: s.values,
        backgroundColor: s.color,
      })),
    }),
    [labels, series],
  )

  const options = useMemo<ChartOptions<'bar'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      layout: {
        padding: { top: valueLabelLines ? TWO_LINE_LABEL_PADDING_TOP : LABEL_PADDING_TOP },
      },
      scales: {
        x: {
          grid: { display: false },
          title: { display: Boolean(xAxisLabel), text: xAxisLabel },
          ticks: { maxRotation: xTickMaxRotation, autoSkip: false },
        },
        y: {
          beginAtZero: true,
          title: { display: true, text: yAxisLabel },
          ticks: { precision: 0, callback: (value) => valueFormatter(Number(value)) },
        },
      },
      plugins: {
        barValueLabels: {
          formatValue: valueFormatter,
          formatLines: valueLabelLines,
          color: valueLabelColor,
        },
        legend:
          series.length > 1
            ? { position: 'top', align: 'end', labels: { usePointStyle: true, boxWidth: 12 } }
            : { display: false },
        tooltip: {
          callbacks: {
            label: (context) =>
              `${context.dataset.label}: ${valueFormatter(context.parsed.y ?? 0)}`,
          },
        },
      },
    }),
    [
      xAxisLabel,
      yAxisLabel,
      series.length,
      valueFormatter,
      valueLabelLines,
      valueLabelColor,
      xTickMaxRotation,
    ],
  )

  return (
    <div style={{ height, position: 'relative' }}>
      <Chart<'bar', number[], BucketLabel>
        type='bar'
        data={data}
        options={options}
        plugins={bucketBarChartPlugins}
      />
    </div>
  )
}
